import React, { useState, useEffect, useCallback } from 'react';
import {
    Container,
    Card,
    Table,
    Button,
    Modal,
    Form,
    Alert,
    Badge,
    Spinner,
    Row,
    Col
} from 'react-bootstrap';
import { onboardingService, OnboardingQuestion, OnboardingAnswer } from '../../../services/onboardingService';
import { showSuccess, showError, showDeleteConfirm } from '../../../Utilities/sweetAlert';

const OnboardingQuestions: React.FC = () => {
    const [questions, setQuestions] = useState<OnboardingQuestion[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Modal State
    const [showModal, setShowModal] = useState(false);
    const [editingQuestion, setEditingQuestion] = useState<OnboardingQuestion | null>(null);
    const [modalLoading, setModalLoading] = useState(false);

    // Form State
    const [questionText, setQuestionText] = useState('');
    const [isMultiple, setIsMultiple] = useState(false);
    const [isActive, setIsActive] = useState(true);
    const [answers, setAnswers] = useState<{ id?: number; text: string }[]>([]);

    const fetchQuestions = useCallback(async () => {
        try {
            setLoading(true);
            const response = await onboardingService.getQuestions();
            setQuestions(response.data.questions);
        } catch (err) {
            console.error('Error fetching questions:', err);
            setError('Failed to load questions.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchQuestions();
    }, [fetchQuestions]);

    const handleOpenModal = (question?: OnboardingQuestion) => {
        if (question) {
            setEditingQuestion(question);
            setQuestionText(question.text);
            setIsMultiple(question.isMultiple);
            setIsActive(question.isActive);
            setAnswers(question.answers.map(a => ({ id: a.id, text: a.text })));
        } else {
            setEditingQuestion(null);
            setQuestionText('');
            setIsMultiple(false);
            setIsActive(true);
            setAnswers([{ text: '' }, { text: '' }]); // Default 2 empty answers
        }
        setShowModal(true);
    };

    const handleSave = async () => {
        if (!questionText.trim()) {
            showError('Invalid Input', 'Question text is required.');
            return;
        }

        const validAnswers = answers.filter(a => a.text.trim() !== '');
        if (validAnswers.length < 2) {
            showError('Invalid Input', 'At least 2 answers are required.');
            return;
        }

        try {
            setModalLoading(true);

            if (editingQuestion) {
                // Update
                const updateData = {
                    text: questionText,
                    isMultiple,
                    isActive,
                    answers: validAnswers
                };
                await onboardingService.updateQuestion(editingQuestion.id, updateData as any);
                showSuccess('Success', 'Question updated successfully.');
            } else {
                // Create
                // First create question
                const qResponse = await onboardingService.createQuestion({
                    text: questionText,
                    isMultiple
                });
                const newQuestionId = qResponse.data.question.id;

                // Then create answers
                if (newQuestionId) {
                    await onboardingService.createAnswers({
                        answers: [{
                            question: newQuestionId,
                            answers: validAnswers.map(a => ({ text: a.text }))
                        }]
                    });
                }
                showSuccess('Success', 'Question created successfully.');
            }

            setShowModal(false);
            fetchQuestions();
        } catch (err) {
            console.error('Error saving question:', err);
            showError('Error', 'Failed to save question.');
        } finally {
            setModalLoading(false);
        }
    };

    const handleAnswerChange = (index: number, val: string) => {
        const newAnswers = [...answers];
        newAnswers[index].text = val;
        setAnswers(newAnswers);
    };

    const addAnswerField = () => {
        setAnswers([...answers, { text: '' }]);
    };

    const removeAnswerField = (index: number) => {
        const newAnswers = answers.filter((_, i) => i !== index);
        setAnswers(newAnswers);
    };

    const toggleStatus = async (question: OnboardingQuestion) => {
        try {
            await onboardingService.updateQuestion(question.id, { isActive: !question.isActive });
            showSuccess('Success', `Question is now ${!question.isActive ? 'Active' : 'Inactive'}`);
            fetchQuestions();
        } catch (err) {
            showError('Error', 'Failed to update status.');
        }
    };

    if (loading) {
        return (
            <Container fluid className="py-4 text-center">
                <Spinner animation="border" />
            </Container>
        );
    }

    return (
        <Container fluid className="py-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h1>Onboarding Questions</h1>
                <Button variant="primary" onClick={() => handleOpenModal()}>+ Add Question</Button>
            </div>

            {error && <Alert variant="danger">{error}</Alert>}

            <Card>
                <Card.Body className="p-0">
                    <Table striped hover responsive className="mb-0">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Question</th>
                                <th>Type</th>
                                <th>Answers</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {questions.map(q => (
                                <tr key={q.id}>
                                    <td>{q.id}</td>
                                    <td>{q.text}</td>
                                    <td><Badge bg="info">{q.isMultiple ? 'Multiple Choice' : 'Single Select'}</Badge></td>
                                    <td>
                                        <ul className="list-unstyled mb-0">
                                            {q.answers?.map(a => <li key={a.id}>- {a.text}</li>)}
                                        </ul>
                                    </td>
                                    <td>
                                        <Badge bg={q.isActive ? 'success' : 'secondary'}>
                                            {q.isActive ? 'Active' : 'Inactive'}
                                        </Badge>
                                    </td>
                                    <td>
                                        <Button variant="outline-primary" size="sm" className="me-2" onClick={() => handleOpenModal(q)}>
                                            <i className="fas fa-edit"></i>
                                        </Button>
                                        <Button
                                            variant={q.isActive ? "outline-warning" : "outline-success"}
                                            size="sm"
                                            onClick={() => toggleStatus(q)}
                                        >
                                            <i className={`fas ${q.isActive ? 'fa-ban' : 'fa-check'}`}></i>
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>

            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{editingQuestion ? 'Edit Question' : 'New Question'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Question Text</Form.Label>
                            <Form.Control
                                type="text"
                                value={questionText}
                                onChange={e => setQuestionText(e.target.value)}
                                placeholder="Enter question..."
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Check
                                type="switch"
                                id="multiple-switch"
                                label="Allow Multiple Selections"
                                checked={isMultiple}
                                onChange={e => setIsMultiple(e.target.checked)}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Check
                                type="switch"
                                id="active-switch"
                                label="Is Active"
                                checked={isActive}
                                onChange={e => setIsActive(e.target.checked)}
                            />
                        </Form.Group>

                        <hr />
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h5>Answers</h5>
                            <Button variant="outline-secondary" size="sm" onClick={addAnswerField}>+ Add Option</Button>
                        </div>

                        {answers.map((ans, idx) => (
                            <Row key={idx} className="mb-2 align-items-center">
                                <Col>
                                    <Form.Control
                                        type="text"
                                        value={ans.text}
                                        onChange={e => handleAnswerChange(idx, e.target.value)}
                                        placeholder={`Answer Option ${idx + 1}`}
                                    />
                                </Col>
                                <Col xs="auto">
                                    <Button variant="outline-danger" size="sm" onClick={() => removeAnswerField(idx)}>
                                        <i className="fas fa-trash"></i>
                                    </Button>
                                </Col>
                            </Row>
                        ))}
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)} disabled={modalLoading}>Cancel</Button>
                    <Button variant="primary" onClick={handleSave} disabled={modalLoading}>
                        {modalLoading ? <Spinner size="sm" animation="border" /> : 'Save Changes'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default OnboardingQuestions;
